package com.yogaaicoach.pages.workout

import com.yogaaicoach.pages.workout.data.MediaModificationService
import com.yogaaicoach.pages.workout.data.WorkoutService
import com.yogaaicoach.pages.workout.data.models.Exercise
import com.yogaaicoach.pages.workout.data.models.WorkoutProgressSectionDto
import com.yogaaicoach.pages.workout.workout.ui.MediaActionController
import com.yogaaicoach.pages.workout.workout.ui.WorkoutState
import com.yogaaicoach.pages.workout.workout.ui.model.ExerciseVideoUrls
import com.yogaaicoach.platform.continueAudio
import com.yogaaicoach.platform.continueVideo
import com.yogaaicoach.platform.pauseAudio
import com.yogaaicoach.platform.pauseVideo
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

// JS-specific ViewModel for WorkoutPage. Mirrors WorkoutViewModel (commonMain) but exposes
// StateFlow instead of Compose MutableState so React can observe state via collect{}.
@OptIn(ExperimentalUuidApi::class)
internal class WorkoutScreenViewModel(
    private val workoutService: WorkoutService,
    mediaModificationService: MediaModificationService,
    private val applicationScope: CoroutineScope,
) {
    private val viewModelScope = MainScope()
    private val mediaActionController = MediaActionController(mediaModificationService)

    val isPageLoaded = MutableStateFlow(false)
    val workoutState = MutableStateFlow(WorkoutState.NOT_STARTED)
    val currentExercise = MutableStateFlow<Exercise?>(null)
    val nextExercise = MutableStateFlow<Exercise?>(null)
    val countdownSeconds = MutableStateFlow(0)
    val isPaused = MutableStateFlow(false)
    val isFirstExercise = MutableStateFlow(true)
    val isCurrentExerciseHeavy = MutableStateFlow(false)
    val sections = MutableStateFlow<List<WorkoutProgressSectionDto>>(emptyList())
    val videoUrl = MutableStateFlow<String?>(null)
    val audioUrl = MutableStateFlow<String?>(null)

    private val isVideoReady = MutableStateFlow(false)
    private val isAudioReady = MutableStateFlow(false)
    private var timerJob: Job? = null
    private var freezeSecondsLeft = 0
    private var mediaReadyJob: Job? = null

    private var exercises: List<Exercise> = emptyList()
    private var currentExerciseIndex = 0
    private var loadedWorkoutGuid: Uuid? = null
    private var isPortraitView = false
    private var isLoading = false
    private var currentVideoUrls: ExerciseVideoUrls? = null
    private var currentExerciseDuration = 0

    fun init(workoutGuid: Uuid, startExerciseNumber: Int, isPortrait: Boolean) {
        if (isPageLoaded.value || isLoading) return
        isLoading = true
        isPortraitView = isPortrait
        loadedWorkoutGuid = workoutGuid

        viewModelScope.launch {
            val workoutData = workoutService.getWorkoutData(workoutGuid, false) ?: run {
                isLoading = false
                return@launch
            }
            exercises = workoutData.exercises
            sections.value = workoutData.sections
            currentExerciseIndex = startExerciseNumber - 1
            updateCurrentAndNext()
            loadMediaForCurrentExercise()
            workoutState.value = WorkoutState.IN_PROGRESS
            isPageLoaded.value = true
            isLoading = false
            waitForMediaThenStartTimer()
        }
    }

    fun onVideoReady() { isVideoReady.value = true }
    fun onAudioReady() { isAudioReady.value = true }

    private suspend fun loadMediaForCurrentExercise() {
        val exercise = currentExercise.value ?: return
        // Clear first: currentExercise has already moved on, so leaving the previous exercise's
        // URL in place would make the page load it into the video slot before the new one lands.
        videoUrl.value = null
        currentVideoUrls = null
        // Stop the outgoing exercise while its successor's URLs are being fetched. Beyond the
        // audio bleeding over, a video left running can still reach its end and fire `ended`
        // into the transition listener the previous exercise armed.
        pauseVideo()
        pauseAudio()
        coroutineScope {
            launch {
                val urls = workoutService.getExerciseVideoUrls(exercise.baseData.video, isPortraitView)
                currentVideoUrls = urls
                // Only the first part goes on screen now; the rest are handed to the action
                // controller, which preloads and swaps them as the exercise plays.
                videoUrl.value = urls?.initialUrl()
            }
            launch {
                audioUrl.value = workoutService.getAudioUrl(exercise.baseData.audio.filename)
            }
        }
        mediaActionController.loadActionsFor(exercise, currentVideoUrls)
    }

    private fun waitForMediaThenStartTimer() {
        mediaReadyJob?.cancel()
        mediaReadyJob = viewModelScope.launch {
            isVideoReady.combine(isAudioReady) { v, a -> v && a }
                .filter { it }
                .first()
            startTimer()
        }
    }

    private fun startTimer() {
        val duration = (currentExercise.value?.durationInSeconds ?: 0) *
                (currentExercise.value?.repetitions ?: 1)
        currentExerciseDuration = duration
        countdownSeconds.value = duration
        // The second-0 actions — playback speed correction, preloading the next video part, and
        // the `ended` listener that drives intro → loop → outro — have to be armed before
        // playback starts, so they run even for an exercise that begins while paused.
        mediaActionController.onTick(0, duration)
        freezeSecondsLeft = mediaActionController.startFreezeSeconds
        if (isPaused.value) {
            // Paused while this exercise's media was still loading — leave both elements where
            // loading left them and only arm the duration, so a later resume plays this exercise.
            pauseVideo()
            pauseAudio()
        } else {
            // Pause up front rather than leaving it to the loop: the element is already autoplaying
            // by now, so a coroutine hop would show a frame or two of movement before the hold.
            if (freezeSecondsLeft > 0) {
                mediaActionController.beginStartFreeze()
            } else {
                continueVideo()
                continueAudio()
            }
            runTimerLoop()
        }
    }

    private fun runTimerLoop() {
        timerJob?.cancel()
        timerJob = viewModelScope.launch {
            // The countdown does not run during the freeze, so the held frame is extra time rather
            // than time taken away from the exercise. Counting down freezeSecondsLeft keeps the
            // remainder if this job is cancelled by a pause, so resuming finishes the freeze.
            if (freezeSecondsLeft > 0) {
                mediaActionController.beginStartFreeze()
                while (freezeSecondsLeft > 0) {
                    delay(1000)
                    freezeSecondsLeft--
                }
                mediaActionController.endStartFreeze()
            }

            while (countdownSeconds.value > 0) {
                delay(1000)
                countdownSeconds.value--
                mediaActionController.onTick(
                    elapsedSeconds = currentExerciseDuration - countdownSeconds.value,
                    remainingSeconds = countdownSeconds.value,
                )
            }
            onExerciseFinished()
        }
    }

    private suspend fun onExerciseFinished() {
        currentExercise.value?.workoutExerciseGuid?.let { guid ->
            applicationScope.launch { workoutService.completeWorkoutExercise(guid) }
        }
        currentExerciseIndex++
        if (currentExerciseIndex < exercises.size) {
            isCurrentExerciseHeavy.value = false
            // A new exercise always starts playing, so a skip taken while paused leaves the
            // pause state behind — otherwise the PAUSED overlay and the play icon stay on
            // while the new exercise is actually running.
            isPaused.value = false
            updateCurrentAndNext()
            isVideoReady.value = false
            isAudioReady.value = false
            loadMediaForCurrentExercise()
            waitForMediaThenStartTimer()
        } else {
            finishWorkout()
        }
    }

    fun onSkipExercise() {
        timerJob?.cancel()
        viewModelScope.launch { onExerciseFinished() }
    }

    fun onPreviousExercise() {
        if (currentExerciseIndex <= 0) return
        timerJob?.cancel()
        currentExerciseIndex--
        isCurrentExerciseHeavy.value = false
        isPaused.value = false
        updateCurrentAndNext()
        isVideoReady.value = false
        isAudioReady.value = false
        viewModelScope.launch {
            loadMediaForCurrentExercise()
            waitForMediaThenStartTimer()
        }
    }

    fun toggleIsPaused() {
        isPaused.value = !isPaused.value
        if (isPaused.value) {
            timerJob?.cancel()
            pauseVideo()
            pauseAudio()
        } else {
            // While frozen the media is meant to stay on the first frame — the timer loop resumes
            // it when the freeze runs out.
            if (freezeSecondsLeft == 0) {
                continueVideo()
                continueAudio()
            }
            runTimerLoop()
        }
    }

    fun onMarkHeavy(isHeavy: Boolean) {
        val guid = currentExercise.value?.workoutExerciseGuid ?: return
        isCurrentExerciseHeavy.value = isHeavy
        applicationScope.launch { workoutService.markWorkoutExerciseHeavy(guid, isHeavy) }
    }

    fun completeWorkout(onNavigate: () -> Unit) {
        finishWorkout()
        onNavigate()
    }

    // Both endings — the last exercise running out and an explicit complete — have to report the
    // workout as done to the backend. The guid is read before the state flips because observers of
    // COMPLETED navigate away, which resets it.
    private fun finishWorkout() {
        timerJob?.cancel()
        mediaReadyJob?.cancel()
        loadedWorkoutGuid?.let { guid ->
            applicationScope.launch { workoutService.completeWorkout(guid) }
        }
        workoutState.value = WorkoutState.COMPLETED
    }

    fun reset() {
        timerJob?.cancel()
        mediaReadyJob?.cancel()
        isPageLoaded.value = false
        workoutState.value = WorkoutState.NOT_STARTED
        currentExercise.value = null
        nextExercise.value = null
        countdownSeconds.value = 0
        isPaused.value = false
        isFirstExercise.value = true
        isCurrentExerciseHeavy.value = false
        videoUrl.value = null
        audioUrl.value = null
        currentVideoUrls = null
        currentExerciseDuration = 0
        isVideoReady.value = false
        isAudioReady.value = false
        sections.value = emptyList()
        exercises = emptyList()
        currentExerciseIndex = 0
        loadedWorkoutGuid = null
        isLoading = false
    }

    private fun updateCurrentAndNext() {
        currentExercise.value = exercises.getOrNull(currentExerciseIndex)
        nextExercise.value = exercises.getOrNull(currentExerciseIndex + 1)
        isFirstExercise.value = currentExerciseIndex == 0
    }
}
